/* eslint-disable max-lines */
import {
  REPLENISH_APPLY_ITEMS_SCOPE,
  CompositionParams,
  ENTITY_TYPES,
  GridRowData,
  IAutoRestockReplenishParams,
  IBaseEntityResponse,
  IBaseEntityType,
  IBaseFormService,
  IBulkReplenishOption,
  IconsColumnInfo,
  IEvent,
  InitialFactorAndCountValue,
  IQohInventoryItemVenue,
  ISite,
  ITransferAdjustReplenishOptions,
  ITransferData,
  ITransferErrorMapper,
  ITransferReplenishCalculationFormulaParams,
  ITransferReplenishCalculationFormulaParamsForRestock,
  ITransferReplenishGettingParams,
  IXVIListColumn,
  ObjectId,
  REPLENISH_TYPE_VALUES,
  SiteType,
  TooltipIconConfig,
  TRANSFER_PAR_QTY_COLUMN_HEADER,
  TRANSFER_PDF_HEADER_VALUES,
  TRANSFER_TYPE,
  ExpandMeasure,
  MEASURE_TRANSACTION,
  DATA_SOURCE,
  TRANSFER_TYPE_ENUM_OPTIONS,
  TRANSFER_STATUS_ENUM_OPTIONS,
  ValueFormatterFn,
  DATA_SOURCE_ENUM_OPTIONS,
  TRANSFER_QTY_PULLED_COLUMN_HEADER,
} from '@app/shared/types';
import { GridApi, ValueFormatterParams } from 'ag-grid-community';
import {
  assign,
  concat,
  filter,
  find,
  findIndex,
  forEach,
  get,
  gt,
  gte,
  has,
  head,
  includes,
  intersectionBy,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isInteger,
  isNil,
  isNumber,
  lt,
  map,
  partition,
  reduce,
  set,
  some,
  sortBy,
  tail,
  toLower,
  toString,
  uniqBy,
} from 'lodash';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { TransferFormService } from '@shared/services/transfer/transfer.form.service';
import { TransferDestinationsFormService } from '@shared/services/transfer-destinations/transfer-destinations.form.service';
import { DecimalNumber } from '@app/shared/common';
import {
  InventoryItemVenueMeasure,
  Threshold,
} from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import {
  TRANSFER_FINAL_STATUS,
  TRANSFER_OPEN_PENDING_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { IPdfValueParams } from '@shared/common/make-pdf/v2';
import { ThresholdType } from '@shared/types/enums/threshold-type.enum';
import {
  ENTITY_ID_PATH,
  MAX_INT_FOR_TRANSFER_QUANTITY,
  MAX_NUMBER_FOR_TRANSFER_QUANTITY,
  MIN_INT_FOR_TRANSFER_QUANTITY,
  MIN_NUMBER_FOR_TRANSFER_QUANTITY,
} from '@config/global-constants';
import { findCurrentFactorValue } from '../orders/order.util';
import { generateSpacesText } from '@app/shared/utils/common-utils';
import { StateService } from '../state-service';
import { AUTO_COMPLETE } from './transfer-constants';
import { EVENT_STATUS_VALUE } from '@shared/types/enums/event-status.enum';
import { TransferGroupFormService } from '@shared/services/transfer-group/transfer-group.form.service';
import { TransferBaseFormService } from '@shared/services/transfer-common';
import { IPdfTableHeader } from '@app/shared/common/make-pdf/pdf-table-header.type';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { TRANSFER_FORM_FIELD_PATHS } from './transfer-form-field-paths';
import { getDefaultMeasure } from '@shared/utils/measure.utils';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

type CalculateFun = (params: Partial<ValueFormatterParams>) => DecimalNumber;

// eslint-disable-next-line
export const calculateUnitCost: CalculateFun = (params): DecimalNumber => {
  const transferMeasures = filter(
    params.data?.measures,
    (item) => item.transactions.transfer,
  );
  const costsParam = find(params.data?.costs, 'is_preferred');
  const costValue = new DecimalNumber(get(costsParam, 'cost_value', 0));
  const unitCost = costValue.mul(
    get(
      !params.data?.measure_entity_id
        ? getDefaultMeasure(transferMeasures, MEASURE_TRANSACTION.Transfer)
        : find(
            transferMeasures,
            (measure) =>
              // CAUTION!! this logic maybe only use for detail transfer page or print pdf
              isEqual(measure.value, params.data?.measure_entity_id) ||
              isEqual(measure.entity_id, params.data?.measure_entity_id),
          ),
      'factor_value',
      0,
    ),
  );

  return unitCost;
};

export const isXVIStructureDoc = (
  type: TRANSFER_TYPE,
  status: TRANSFER_STATUS_VALUE,
): boolean => {
  return isEqual(TRANSFER_TYPE.PRE_EVENT_TRANSFER, type)
    ? includes(TRANSFER_FINAL_STATUS, status)
    : includes([TRANSFER_STATUS_VALUE.Void], status);
};

export const getIsShowReceivedColStatus = (
  type: TRANSFER_TYPE,
  status: TRANSFER_STATUS_VALUE,
): boolean => {
  // TRANSFER_TYPE.PRE_EVENT_TRANSFER not shown as quantity received
  // TRANSFER_STATUS_VALUE.Canceled not shown as quantity received
  // is submitted status will shown as quantity received
  return (
    !isEqual(TRANSFER_TYPE.PRE_EVENT_TRANSFER, type) &&
    includes(
      [
        TRANSFER_STATUS_VALUE.Pending,
        TRANSFER_STATUS_VALUE.Completed,
        TRANSFER_STATUS_VALUE.Rejected,
        TRANSFER_STATUS_VALUE.Void,
      ],
      status,
    )
  );
};

export const calculatePendingQuantity = (
  initialCount: DecimalNumber,
  factorValue: DecimalNumber,
): DecimalNumber => new DecimalNumber(initialCount || 0).mul(factorValue || 0);

/**
 * @deprecated should use getPendingQuantityMap instead of this, and this should be remove after all switch to new
 */
export const createPendingQuantityMap = (
  formService:
    | TransferFormService
    | TransferDestinationsFormService
    | TransferGroupFormService
    | TransferBaseFormService,
  transferResponse: IBaseEntityResponse,
  siteId: string,
  siteIdTo: string,
): IBaseEntityType => {
  const pendingQtyMap = {};
  forEach(transferResponse.items, (transfer) => {
    forEach(transfer.inventory_items, (inventoryItem) => {
      if (!has(pendingQtyMap, inventoryItem['item_entity_id'])) {
        pendingQtyMap[inventoryItem['item_entity_id']] = {
          [formService.formFieldPath.siteId]: new DecimalNumber(0),
          [formService.formFieldPath.siteIdTo]: new DecimalNumber(0),
        };
      }
      const pendingQuantity = calculatePendingQuantity(
        inventoryItem[formService.formFieldPath.initialCount],
        inventoryItem[formService.formFieldPath.measureFactorValue],
      );

      // Calculate Pending Quantities From
      // If the site_id of the transfer is equal to the siteId, is mean this item transfer to other site.
      if (isEqual(siteId, transfer[formService.formFieldPath.siteId])) {
        pendingQtyMap[inventoryItem['item_entity_id']][
          formService.formFieldPath.siteId
        ] =
          pendingQtyMap[inventoryItem['item_entity_id']][
            formService.formFieldPath.siteId
          ].sub(pendingQuantity);
      }
      // If the site_id_to of the transfer is equal to the site_id, is mean quantity is transfer in from other site.
      if (isEqual(siteId, transfer[formService.formFieldPath.siteIdTo])) {
        pendingQtyMap[inventoryItem['item_entity_id']][
          formService.formFieldPath.siteId
        ] =
          pendingQtyMap[inventoryItem['item_entity_id']][
            formService.formFieldPath.siteId
          ].add(pendingQuantity);
      }
      // Calculate Pending Quantities To
      // If the site_id_to of the transfer is equal to the site_id, is mean this item transfer to other site.
      if (isEqual(siteIdTo, transfer[formService.formFieldPath.siteId])) {
        pendingQtyMap[inventoryItem['item_entity_id']][
          formService.formFieldPath.siteIdTo
        ] =
          pendingQtyMap[inventoryItem['item_entity_id']][
            formService.formFieldPath.siteIdTo
          ].sub(pendingQuantity);
      }
      // If the site_id_to of the transfer is equal to the site_id_to, is mean quantity is transfer in from other site.
      if (isEqual(siteIdTo, transfer[formService.formFieldPath.siteIdTo])) {
        pendingQtyMap[inventoryItem['item_entity_id']][
          formService.formFieldPath.siteIdTo
        ] =
          pendingQtyMap[inventoryItem['item_entity_id']][
            formService.formFieldPath.siteIdTo
          ].add(pendingQuantity);
      }
    });
  });

  return pendingQtyMap;
};

export const getCurrentItemQoh = (
  qohData: IQohInventoryItemVenue[],
  siteId: string,
  rowData: IBaseEntityType,
): IQohInventoryItemVenue => {
  return find(qohData, (item) => {
    return (
      isEqual(item.site_id, siteId) &&
      isEqual(item.inventory_item_entity_id, rowData.entity_id)
    );
  });
};

export const transferTypeFormatter = (
  params: Partial<ValueFormatterParams>,
  options: {
    i18nService: XVII18nService;
  },
): string => {
  return options.i18nService.instant(
    get(TRANSFER_TYPE_ENUM_OPTIONS, params.value, ''),
  );
};

export const isSiteTypePrimaryWarehouse = (site: ISite): boolean => {
  if (isNil(site)) return false;
  const warehouseTypes = [SiteType.PrimaryWarehouse];
  const siteTypes = [site.type, site.business_type];

  return some(siteTypes, (type) => includes(warehouseTypes, type));
};

export const isSiteTypeWarehouse = (site: ISite): boolean => {
  const warehouseTypes = [SiteType.Warehouse, SiteType.PrimaryWarehouse];
  const siteTypes = [site.type, site.business_type];

  return some(siteTypes, (type) => includes(warehouseTypes, type));
};

export const getEventRelatedAndWarehouseSites = (
  allSites: ISite[],
  eventSites: ISite[],
): ISite[] => {
  const assignedWarehouseSites: ISite[] = filter(allSites, (site) =>
    isSiteTypeWarehouse(site),
  );
  const assignedEventSites: ISite[] = intersectionBy(
    allSites,
    eventSites,
    'id',
  );

  return uniqBy(concat(assignedEventSites, assignedWarehouseSites), 'name');
};

export const addStoreNumberForSites = (sites: ISite[]): ISite[] => {
  return map(sites, (site) =>
    assign({}, site, {
      name: generateSpacesText(site.name, site.store_number),
    }),
  );
};

export const setPrimaryAndWarehouseTop = (sites: ISite[]): ISite[] => {
  const sortedSites = sortBy(sites, (site) => toLower(site.name));
  const [warehouseAndPrimaryWarehouseSites, otherSites] = partition(
    sortedSites,
    (site) => isSiteTypeWarehouse(site),
  );

  const [primaryWarehouseSites, warehouseSites] = partition(
    warehouseAndPrimaryWarehouseSites,
    (site) =>
      includes([site.type, site.business_type], SiteType.PrimaryWarehouse),
  );

  return concat(primaryWarehouseSites, warehouseSites, otherSites);
};

export const setPrimaryAndWarehouseTopAndAddStoreNumber = (
  sites: ISite[],
): ISite[] => {
  const sortedSites = setPrimaryAndWarehouseTop(sites);

  return addStoreNumberForSites(sortedSites);
};

export const transferPDFQuantityFormatter = ({
  tools,
  ...params
}: IPdfValueParams): string =>
  isNil(params.value)
    ? ''
    : tools.services.numberFormatService.getNumberFormatter(false)(params);

/**
 * @param item
 * @param measureEntityId
 */
export const findMeasureFactorValue = (
  item: IQohInventoryItemVenue | GridRowData,
  measureEntityId: string,
): number => {
  return (
    find(item.origin_measures as InventoryItemVenueMeasure[], {
      entity_id: measureEntityId,
    })?.factor_value || 0
  );
};

/**
 * @desc generate changed count value with ceil method.
 * @param currentRowData
 * @param maximum we don`t think about maximum precision in transfer unit change.
 * @returns updated row data.
 */
export const handleTransferUnitChange = (
  currentRowData: GridRowData,
  selectedEvent: IEvent,
  isOutsideOfEventTransfer: boolean,
  maximum = MAX_INT_FOR_TRANSFER_QUANTITY,
): GridRowData => {
  const minimum = MIN_INT_FOR_TRANSFER_QUANTITY;
  const currentMeasureEntityId: string =
    currentRowData.measure_entity_id as string;
  const historicalInitialTotal: string =
    currentRowData.historical_initial_total as string;
  const currentFactorValue: number = findMeasureFactorValue(
    currentRowData,
    currentMeasureEntityId,
  );
  const count = get(currentRowData, 'initialFactorAndCountValue.count', null);
  const initialCount = get(
    currentRowData,
    'initialFactorAndCountValue.initial_count',
    null,
  );
  const initFactorValue: number = get(
    currentRowData,
    'initialFactorAndCountValue.factorValue',
    0,
  ) as number;
  const initReceivedFactorValue: number = get(
    currentRowData,
    'initialFactorAndCountValue.receivedFactorValue',
    0,
  ) as number;
  // displayed initial value of field Quantity Request or Quantity Received
  // countOfRoundCeil = init Factor Value * init Quantity(Request | Received) Count / current Factor Value
  let countOfRoundCeil: string | null = isNil(count)
    ? null
    : new DecimalNumber(initReceivedFactorValue || 0)
        .mul(count)
        .div(currentFactorValue)
        .ceil()
        .toString();

  let initialCountOfRoundCeil: string | null = isNil(initialCount)
    ? null
    : new DecimalNumber(initFactorValue || 0)
        .mul(initialCount)
        .div(currentFactorValue)
        .ceil()
        .toString();
  // historicalInitialTotal value is from historical list new transfer
  let historicalInitialCountOfRoundCeil: string | null = isNil(
    historicalInitialTotal,
  )
    ? null
    : new DecimalNumber(historicalInitialTotal || 0)
        .div(currentFactorValue)
        .ceil()
        .toString();
  // if transfer unit change, calculated result greater than maximum should be rewritten as maximum.
  if (gt(countOfRoundCeil, maximum)) countOfRoundCeil = toString(maximum);
  if (gt(initialCountOfRoundCeil, maximum)) {
    initialCountOfRoundCeil = toString(maximum);
  }
  if (gt(historicalInitialCountOfRoundCeil, maximum)) {
    historicalInitialCountOfRoundCeil = toString(maximum);
  }
  if (lt(countOfRoundCeil, minimum)) countOfRoundCeil = toString(minimum);
  if (lt(initialCountOfRoundCeil, minimum)) {
    initialCountOfRoundCeil = toString(minimum);
  }
  if (lt(historicalInitialCountOfRoundCeil, minimum)) {
    historicalInitialCountOfRoundCeil = toString(minimum);
  }
  const originInitialCount = isEqual(currentFactorValue, initFactorValue)
    ? initialCount
    : initialCountOfRoundCeil;
  const currentParQty = getParQty(
    currentRowData as unknown as IQohInventoryItemVenue,
    selectedEvent,
    isOutsideOfEventTransfer,
  );

  return {
    ...currentRowData,
    // initial_count for Quantity Request
    initial_count: isNil(historicalInitialTotal)
      ? originInitialCount
      : historicalInitialCountOfRoundCeil,
    // count for Quantity Received
    count: isEqual(currentFactorValue, initReceivedFactorValue)
      ? count
      : countOfRoundCeil,
    par_qty: currentParQty,
  };
};

export const updateInitialFactorAndCountValue = (
  currentRowData: GridRowData,
  isDuplicateMode: boolean = false,
): InitialFactorAndCountValue => {
  if (isDuplicateMode && get(currentRowData, 'is_measure_changed')) {
    return get(currentRowData, 'initialFactorAndCountValue');
  }

  const {
    measure_entity_id: measureEntityId,
    initial_count,
    count,
  } = currentRowData;
  const currentFactorValue: number = findMeasureFactorValue(
    currentRowData,
    measureEntityId as string,
  );

  return {
    factorValue: currentFactorValue,
    initial_count: initial_count as string,
    receivedFactorValue: currentFactorValue,
    count: count as string,
  };
};

export const appendInitialFactorAndCountValue = (
  inventoryItemVenues: IQohInventoryItemVenue[],
  isDuplicateMode: boolean = false,
  inventoryItemVenueMap: IBaseEntityType = {},
): IQohInventoryItemVenue[] => {
  return map(inventoryItemVenues, (item) => {
    const initMeasure = find(item.origin_measures, (measure) =>
      isEqual(item['measure_entity_id'], measure['entity_id']),
    );

    const newItem = {
      ...item,
      initialFactorAndCountValue: {
        initial_count: get(item, 'initial_count', undefined),
        count: get(item, 'count', undefined),
        factorValue: get(initMeasure, 'factor_value', 0),
        receivedFactorValue: get(initMeasure, 'factor_value', 0),
      },
    };

    if (
      isDuplicateMode &&
      !isEmpty(inventoryItemVenueMap) &&
      get(item, 'is_measure_changed')
    ) {
      set(
        newItem,
        ['initialFactorAndCountValue', 'initial_count'],
        get(inventoryItemVenueMap, [item.entity_id, 'origin_initial_count']),
      );
      set(
        newItem,
        ['initialFactorAndCountValue', 'factorValue'],
        get(inventoryItemVenueMap, [
          item.entity_id,
          'origin_measure_factor_value',
        ]),
      );
      set(
        newItem,
        ['initialFactorAndCountValue', 'receivedFactorValue'],
        get(inventoryItemVenueMap, [
          item.entity_id,
          'origin_measure_factor_value',
        ]),
      );
    }

    return newItem;
  });
};

/**
 * @description use for transfer-pdf fields qoh_from and qoh_to value formatter.
 * @param params value formatter received params.
 * @param selectedKey 'selectedFrom' or 'selectedTo' to choose one field key.
 * @returns stringified number value.
 */
export const calculateResultOfDivFactorValue = (
  data: CompositionParams,
  showPendingQty: boolean,
): string => {
  const { tools, measureFactorValue, value, pendingQty } = data;
  const numberPrecision: number =
    tools.services.stateService.getNumberPrecision();
  const originQoh: DecimalNumber = new DecimalNumber(value || 0);
  const calculatedQohResult: number = originQoh
    .div(measureFactorValue)
    .valueOf();

  const formattedQohFrom: string =
    tools.services.numberFormatService.getNumberFormatter(false)({
      value: calculatedQohResult,
      precision: numberPrecision,
    });
  if (pendingQty && !pendingQty.eq(0) && showPendingQty) {
    const calculatedPendingQty = new DecimalNumber(pendingQty)
      .div(measureFactorValue)
      .valueOf();
    const formattedPendingQty =
      tools.services.numberFormatService.getNumberFormatter(false)({
        value: calculatedPendingQty,
        precision: numberPrecision,
      });

    return `${formattedQohFrom}/${formattedPendingQty}`;
  }

  return formattedQohFrom;
};

export const generateBulkUpdateConfirmMessageParam = (options: {
  action: string;
  did?: string;
  count?: number;
  target?: string;
  i18nService: XVII18nService;
}): Record<string, string | number> => {
  return {
    did: options.i18nService.instant(options.did),
    count: options.count,
    target: options.i18nService.instant(
      options.target ?? I18n.Forms.TransferForm.TransfersBrackets,
    ),
    wantTo: options.i18nService.instant(options.action),
  };
};

export const initInventoryThresholdMapper = (
  items: IQohInventoryItemVenue[],
): ITransferErrorMapper => {
  return reduce(
    items,
    (itemMap, item) => {
      return {
        ...itemMap,
        [item.entity_id]: {
          error: false,
          _id: item._id,
        },
      };
    },
    {},
  );
};

export const getItemThresholdConfig = (
  thresholdItem: Threshold,
  originMeasures: InventoryItemVenueMeasure[],
  cb?,
): IconsColumnInfo => {
  const thresholdMaxValue = get(
    thresholdItem,
    REPLENISH_TYPE_VALUES.MAX,
    false,
  );
  const thresholdMinValue = get(
    thresholdItem,
    REPLENISH_TYPE_VALUES.MIN,
    false,
  );
  const thresholdMeasure = thresholdItem
    ? find(originMeasures, { entity_id: thresholdItem.measure_entity_id })
    : null;
  const data = {
    thresholdMaxValue,
    thresholdMinValue,
    thresholdMeasure,
    hiddenThresholdWarning: isEqual(
      thresholdMaxValue || thresholdMinValue,
      false,
    ),
  };
  if (isFunction(cb)) return cb(data);

  return data;
};

export const getItemThresholdWarning = (params: {
  cellValue: IconsColumnInfo;
  rowData: IQohInventoryItemVenue;
  numberFormatter: ValueFormatterFn;
  i18nService: XVII18nService;
}): TooltipIconConfig | null => {
  const { cellValue, rowData, numberFormatter, i18nService } = params;

  const {
    hiddenThresholdWarning,
    thresholdMaxValue,
    thresholdMinValue,
    thresholdMeasure,
  } = cellValue;
  const { measures, measure_entity_id, initial_count, qohTo } = rowData;
  const [qohValue, pendingQtyValue] = qohTo;
  const currentMeasure = find(measures, { entity_id: measure_entity_id });
  // calculatedQoh = QOH / selected factor value
  const calculatedQoh = numberFormatter({
    value: new DecimalNumber(qohValue).div(currentMeasure.factor_value),
  });
  // calculatedPendingQtyValue = pendingQtyValue / selected factor value
  const calculatedPendingQtyValue = numberFormatter({
    value: new DecimalNumber(pendingQtyValue).div(currentMeasure.factor_value),
  });
  // Quantity Requested number format
  const calculatedQtyRequested = numberFormatter({
    value: new DecimalNumber(initial_count),
  });
  const formatThresholdValue = (value: number | boolean) => {
    return isNumber(value) ? numberFormatter({ value }) : value;
  };
  let thresholdMaxResult: DecimalNumber;
  let thresholdMinResult: DecimalNumber = new DecimalNumber(0);
  const maxNumberForTransferQuantity = new DecimalNumber(
    MAX_NUMBER_FOR_TRANSFER_QUANTITY,
  ).floor();
  const minNumberForTransferQuantity = new DecimalNumber(
    MIN_NUMBER_FOR_TRANSFER_QUANTITY,
  ).ceil();

  if (
    hiddenThresholdWarning ||
    isNil(initial_count) ||
    thresholdMinResult.eq(initial_count) ||
    isNil(thresholdMeasure) ||
    isNil(currentMeasure)
  ) {
    return null;
  }

  // thresholdMaxResult =
  // (thresholdMaxValue * thresholdMeasure.factor_value - qohValue - pendingQtyValue) / currentMeasure.factor_value
  // eslint-disable-next-line prefer-const
  thresholdMaxResult = isNumber(thresholdMaxValue)
    ? new DecimalNumber(thresholdMaxValue)
        .mul(thresholdMeasure.factor_value)
        .sub(qohValue)
        .sub(pendingQtyValue)
        .div(currentMeasure.factor_value)
        .floor()
    : new DecimalNumber(MAX_NUMBER_FOR_TRANSFER_QUANTITY);

  // thresholdMinResult =
  // (thresholdMinValue * thresholdMeasure.factor_value - qohValue - pendingQtyValue) / currentMeasure.factor_value
  thresholdMinResult = isNumber(thresholdMinValue)
    ? new DecimalNumber(thresholdMinValue)
        .mul(thresholdMeasure.factor_value)
        .sub(qohValue)
        .sub(pendingQtyValue)
        .div(currentMeasure.factor_value)
        .ceil()
    : new DecimalNumber(MIN_NUMBER_FOR_TRANSFER_QUANTITY);

  if (
    (thresholdMinResult.gte(maxNumberForTransferQuantity) &&
      maxNumberForTransferQuantity.lte(initial_count)) ||
    (thresholdMaxResult.lte(minNumberForTransferQuantity) &&
      minNumberForTransferQuantity.gte(initial_count)) ||
    (thresholdMinResult.lte(initial_count) &&
      thresholdMaxResult.gte(initial_count))
  ) {
    return null;
  }

  const isQuantityGreaterThanMaxResult = thresholdMaxResult.lt(initial_count);
  const currentUnitText = `${currentMeasure.description}[${numberFormatter({ value: currentMeasure.factor_value.toString() })}]`;
  const thresholdUnitText = `${thresholdMeasure.description}[${numberFormatter({ value: thresholdMeasure.factor_value.toString() })}]`;

  const warningInfo = {
    qohValue: calculatedQoh,
    qohText: currentUnitText,
    pendingQtyValue: calculatedPendingQtyValue,
    pendingQtyText: currentUnitText,
    requestedQtyValue: calculatedQtyRequested,
    requestedQtyText: currentUnitText,
    thresholdLabel: i18nService.translate(
      isQuantityGreaterThanMaxResult
        ? I18n.Common.Maximum
        : I18n.Common.Minimum,
    ),
    thresholdValue: isQuantityGreaterThanMaxResult
      ? formatThresholdValue(thresholdMaxValue)
      : formatThresholdValue(thresholdMinValue),
    thresholdText: thresholdUnitText,
    // eslint-disable-next-line no-nested-ternary
    thresholdMessage: isQuantityGreaterThanMaxResult
      ? thresholdMaxResult.lt(minNumberForTransferQuantity)
        ? i18nService.translate(
            I18n.InfoMessages.ConsiderIncreasingYourQuantityRequestedTo,
            {
              count: '-999999',
            },
          )
        : i18nService.translate(
            I18n.InfoMessages.ConsiderReducingYourQuantityRequestedToLessThan,
            {
              count: numberFormatter({ value: thresholdMaxResult.valueOf() }),
            },
          )
      : thresholdMinResult.gt(maxNumberForTransferQuantity)
        ? i18nService.translate(
            I18n.InfoMessages.ConsiderReducingYourQuantityRequestedTo,
            {
              count: '999999',
            },
          )
        : i18nService.translate(
            I18n.InfoMessages
              .ConsiderIncreasingYourQuantityRequestedToGreaterThan,
            {
              count: numberFormatter({ value: thresholdMinResult.valueOf() }),
            },
          ),
  };

  const template = `
  <div class="text-left">
    <div>${i18nService.translate(I18n.Forms.TransferForm.QuantityOnHand)}: ${warningInfo.qohValue} (${warningInfo.qohText})</div>
    <div>${i18nService.translate(I18n.Forms.TransferForm.PendingQuantity)}: ${warningInfo.pendingQtyValue} (${warningInfo.pendingQtyText})</div>
    <div>${i18nService.translate(I18n.Forms.TransferForm.QuantityRequested)}: ${warningInfo.requestedQtyValue} (${warningInfo.requestedQtyText})</div>
    <div>${warningInfo.thresholdLabel} ${i18nService.translate(I18n.Forms.GridForm.Threshold)}: ${warningInfo.thresholdValue} (${warningInfo.thresholdText})</div>
    <div class="mt-10">${warningInfo.thresholdMessage}</div>
  </div>`;

  return {
    icon: 'icon-warning',
    class: 'xvi-grid-dark-warning-icon',
    tooltipConfig: {
      template,
      container: 'xvi-grid-dark-warning-icon',
      containerClass: 'flex-container',
    },
  };
};

export const getItemDmWarning = (): TooltipIconConfig => {
  return {
    icon: 'icon-warning',
    class: 'xvi-grid-warn-icon',
  };
};
/**
 * @description calculate QOH and pending Qty in transfer pending and open status.
 * @param params ValueFormatterParams.
 * @param type judge QOH From or QOH To.
 * @param numberFormatService
 * @returns displayed Qoh and pending Qty.
 */
export const qohResultOfDivFactorValue = (
  params: ValueFormatterParams,
  type: string,
  numberFormatService,
): string => {
  const [qohData, pendingQtyData] = params.data[type] ?? [];
  const factorValue = findCurrentFactorValue(
    params.data,
    params.data.measure_entity_id,
  );
  // displayed QOH - To and QOH - From: QOH / Factor Value.
  const calculatedQoh = new DecimalNumber(qohData || 0)
    .div(factorValue)
    .valueOf();
  const formattedQoh = numberFormatService.getNumberFormatter(false)({
    value: calculatedQoh,
  });
  // displayed pending QTY = pending QTY / Factor Value.
  const calculatedPendingQty = new DecimalNumber(pendingQtyData || 0)
    .div(factorValue)
    .valueOf();
  const formattedPendingQty = numberFormatService.getNumberFormatter(false)({
    value: calculatedPendingQty,
  });
  const formattedPendingQtyData = !isEqual(+pendingQtyData, 0)
    ? `/${formattedPendingQty}`
    : '';

  return `${formattedQoh || 0}${formattedPendingQtyData}`;
};

/**
 * @param items
 * @param replenishType
 * @param isOutsideOfEventTransfer
 * @param selectedEvent
 */
export const hasThresholdInItems = (
  items: IQohInventoryItemVenue[],
  replenishType: REPLENISH_TYPE_VALUES = REPLENISH_TYPE_VALUES.PAR,
  isOutsideOfEventTransfer: boolean,
  selectedEvent: IEvent,
  replenishItemOption: REPLENISH_APPLY_ITEMS_SCOPE = REPLENISH_APPLY_ITEMS_SCOPE.ALL_ITEMS,
  // eslint-disable-next-line max-params
): boolean => {
  const isKeepAllItems = isEqual(
    replenishItemOption,
    REPLENISH_APPLY_ITEMS_SCOPE.ALL_ITEMS,
  );
  const filteredItems = isKeepAllItems
    ? items
    : filter(items, {
        is_chargeable: isEqual(
          replenishItemOption,
          REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
        ),
      });

  return some(filteredItems, (item: IQohInventoryItemVenue) => {
    const itemThreshold: Threshold = getItemThreshold(
      item,
      isOutsideOfEventTransfer,
      selectedEvent,
    );

    return has(itemThreshold, replenishType);
  });
};

/**
 * @deprecated should use getItemThreshold in the replenish utils instead
 * @param item Inventory Item
 * @param isOutsideOfEventTransfer
 * @param selectedEvent
 */
export const getItemThreshold = (
  item: IQohInventoryItemVenue,
  isOutsideOfEventTransfer: boolean,
  selectedEvent?: IEvent,
): Threshold => {
  const thresholds: Threshold[] = get(item, 'thresholds', []);
  let threshold: Threshold;

  if (isOutsideOfEventTransfer) {
    threshold = find(thresholds, { type: ThresholdType.Default });
  } else {
    threshold = selectedEvent
      ? find(thresholds, {
          type: ThresholdType.EventType,
          event_type_entity_id: selectedEvent.event_type_entity_id,
        })
      : null;
  }

  return threshold || null;
};

/**
 * 'Quantity Requested' calculation formula:
 * BA: [(PAR/MIN) * Factor value - QOH(To) + Pending quantities(To)] / Factor value of transfer unit
 * Dev: [replenishQuantity * thresholdsMeasureFactorValue - qohTo - pendingQtyTo] / selectedMeasureFactorValue
 *
 * @param replenishQuantity
 * @param thresholdsMeasureFactorValue
 * @param qohTo
 * @param pendingQtyTo
 * @param selectedMeasureFactorValue
 */
export const replenishByCalculationFormula = ({
  replenishQuantity,
  pendingQtyTo,
  qohTo,
  replenishPar,
  replenishMin,
  selectedMeasureFactorValue,
  thresholdsMeasureFactorValue,
}: ITransferReplenishCalculationFormulaParams): DecimalNumber => {
  // [replenishQuantity * thresholdsMeasureFactorValue]
  let replenishmentValue: DecimalNumber = replenishQuantity.mul(
    thresholdsMeasureFactorValue,
  );
  // [replenishQuantity * thresholdsMeasureFactorValue - qohTo]
  replenishmentValue = replenishmentValue.sub(qohTo);
  // [replenishQuantity * thresholdsMeasureFactorValue - qohTo - pendingQtyTo]
  replenishmentValue = replenishmentValue.sub(pendingQtyTo);
  // [replenishQuantity * thresholdsMeasureFactorValue - qohTo - pendingQtyTo] / selectedMeasureFactorValue
  replenishmentValue = replenishmentValue.div(selectedMeasureFactorValue);
  const isIntegerOfReplenishValue = isInteger(replenishmentValue.valueOf());
  // Round up, integer should not round up
  replenishmentValue = isIntegerOfReplenishValue
    ? replenishmentValue
    : replenishmentValue.ceil();

  // Invalid value
  if (!replenishmentValue.isFinite) {
    return new DecimalNumber(0);
  }

  const maxNumber = new DecimalNumber(MAX_NUMBER_FOR_TRANSFER_QUANTITY).floor();
  // over the maximum boundary
  if (replenishmentValue.gt(maxNumber)) {
    return maxNumber;
  }

  // “QOH(To) + Pending Qty(To)” is above or equal to Min, populate Qty Requested with 0.
  if (
    replenishMin &&
    !isNaN(replenishMin.valueOf()) &&
    gte(qohTo.add(pendingQtyTo), 0) &&
    gte(
      qohTo.add(pendingQtyTo),
      replenishMin.mul(thresholdsMeasureFactorValue).valueOf(),
    )
  ) {
    return new DecimalNumber(0);
  }

  const formattedPendingQtyTo = new DecimalNumber(pendingQtyTo || 0);
  const formattedQohTo = new DecimalNumber(qohTo || 0);

  if (!isIntegerOfReplenishValue) {
    replenishmentValue = adjustReplenishmentValue({
      replenishPar,
      replenishmentValue,
      selectedMeasureFactorValue,
      thresholdsMeasureFactorValue,
      valueOfParticipateInCalculations:
        formattedPendingQtyTo.add(formattedQohTo),
    });
  }
  // over the minimum boundary

  return replenishmentValue.gt(0) ? replenishmentValue : new DecimalNumber(0);
};

/**
 *
 * @deprecated we need switch replenish to new version(replenishByCalculationFormulaForRestock instead)
 * @returns the final replenish value.
 */
export const replenishByCalculationFormulaForRestock = ({
  // #TODO is not needed
  standWorksheetItems,
  lastEventOfDestination,
  replenishPercent,
  thresholdsMeasureFactorValue,
  selectedMeasureFactorValue,
  itemThreshold,
  standWorksheetItemsMap,
  item,
}: ITransferReplenishCalculationFormulaParamsForRestock): DecimalNumber => {
  let replenishmentValue: DecimalNumber;
  let valueInCalculationsOfAdjustReplenish = 0;
  let isIntegerOfReplenishValue = false;
  const replenishMin = new DecimalNumber(
    get(itemThreshold, REPLENISH_TYPE_VALUES.MIN),
  );
  const replenishPar = new DecimalNumber(
    get(itemThreshold, REPLENISH_TYPE_VALUES.PAR),
  );
  // “QOH(To) + Pending Qty(To)” is above or equal to Min, populate Qty Requested with 0.
  const qohTo = new DecimalNumber(head(get(item, 'qohTo')));
  const pendingQtyTo = new DecimalNumber(tail(get(item, 'qohTo')));
  if (
    !isNaN(replenishMin.valueOf()) &&
    gte(qohTo.add(pendingQtyTo), 0) &&
    gte(
      qohTo.add(pendingQtyTo),
      replenishMin.mul(thresholdsMeasureFactorValue).valueOf(),
    )
  ) {
    return new DecimalNumber(0);
  }

  if (isEmpty(standWorksheetItems) || isNil(lastEventOfDestination)) {
    // If last stand worksheet or last event based on event sales start time
    // does not exist or cannot be found
    // Quantity Request = (PAR * percentage * factor value of PAR measure) / factor value of Transfer Unit
    replenishmentValue = replenishPar
      .mul(replenishPercent.div(100))
      .mul(thresholdsMeasureFactorValue)
      .div(selectedMeasureFactorValue);
    // is integer should not round up, otherwise round up
    isIntegerOfReplenishValue = isInteger(replenishmentValue.valueOf());
    replenishmentValue = isIntegerOfReplenishValue
      ? replenishmentValue
      : replenishmentValue.ceil();
  } else {
    // If status of last event based on event sales start time is open
    // Quantity Request =
    // (PAR * percentage * factor value of PAR measure - Expected Qty) / factor value of Transfer Unit
    if (isEqual(lastEventOfDestination.status, EVENT_STATUS_VALUE.Open)) {
      valueInCalculationsOfAdjustReplenish = get(
        standWorksheetItemsMap[item.entity_id],
        'expected_qty',
        0,
      );

      replenishmentValue = replenishPar
        .mul(replenishPercent.div(100))
        .mul(thresholdsMeasureFactorValue)
        .sub(valueInCalculationsOfAdjustReplenish)
        .div(selectedMeasureFactorValue);
      // is integer should not round up, otherwise round up
      isIntegerOfReplenishValue = isInteger(replenishmentValue.valueOf());
      replenishmentValue = isIntegerOfReplenishValue
        ? replenishmentValue
        : replenishmentValue.ceil();
    }

    // If status of last event based on event sales start time is closed
    // Quantity Request =
    // (PAR * percentage * factor value of PAR measure - Ending Count) / factor value of Transfer Unit
    if (isEqual(lastEventOfDestination.status, EVENT_STATUS_VALUE.Closed)) {
      valueInCalculationsOfAdjustReplenish = get(
        standWorksheetItemsMap[item.entity_id],
        'end',
        0,
      );

      replenishmentValue = replenishPar
        .mul(replenishPercent.div(100))
        .mul(thresholdsMeasureFactorValue)
        .sub(valueInCalculationsOfAdjustReplenish)
        .div(selectedMeasureFactorValue);
      // is integer should not round up, otherwise round up
      isIntegerOfReplenishValue = isInteger(replenishmentValue.valueOf());
      replenishmentValue = isIntegerOfReplenishValue
        ? replenishmentValue
        : replenishmentValue.ceil();
    }
  }

  // is not isIntegerOfReplenishValue before not round,should adjust the replenish value.
  if (!isIntegerOfReplenishValue) {
    const replenishAdjustmentOptions = {
      replenishmentValue,
      replenishPar: new DecimalNumber(
        get(itemThreshold, REPLENISH_TYPE_VALUES.PAR),
      ),
      selectedMeasureFactorValue: new DecimalNumber(
        selectedMeasureFactorValue || 0,
      ),
      thresholdsMeasureFactorValue: new DecimalNumber(
        thresholdsMeasureFactorValue || 0,
      ),
      valueOfParticipateInCalculations: new DecimalNumber(
        valueInCalculationsOfAdjustReplenish,
      ),
    };
    replenishmentValue = adjustReplenishmentValue(replenishAdjustmentOptions);
  }

  return replenishmentValue;
};

export const adjustReplenishmentValue = (
  adjustOptions: ITransferAdjustReplenishOptions,
): DecimalNumber => {
  const {
    replenishPar,
    replenishmentValue,
    selectedMeasureFactorValue,
    thresholdsMeasureFactorValue,
    // valueOfParticipateInCalculations may come from:
    // case1: qohTo + pendingQtyTo, normal replenish.
    // case2: Expected Qty, replenish for open event & auto restock.
    // case3:  Ending Count, replenish for closed event & auto restock.
    valueOfParticipateInCalculations,
  } = adjustOptions;
  // if adjust replenish value:
  // next replenish value * factor value + QOH > origin replenish par * factor thresholds measure factor value
  const isNeedAdjustValue =
    gt(
      replenishmentValue
        .mul(selectedMeasureFactorValue)
        .add(valueOfParticipateInCalculations),
      replenishPar.mul(thresholdsMeasureFactorValue),
    ) && gt(replenishmentValue, 0);

  return isNeedAdjustValue ? replenishmentValue.sub(1) : replenishmentValue;
};

export const getReplenishedInventoryItems = ({
  items,
  isOutsideOfEventTransfer,
  selectedEvent,
  replenishType = REPLENISH_TYPE_VALUES.PAR,
  formFieldPath,
  replenishPercent,
  replenishItemOption,
}: ITransferReplenishGettingParams): IQohInventoryItemVenue[] => {
  return map(items, (item: IQohInventoryItemVenue) => {
    const itemThreshold: Threshold = getItemThreshold(
      item,
      isOutsideOfEventTransfer,
      selectedEvent,
    );

    if (isNil(get(itemThreshold, replenishType))) {
      return item;
    }

    if (
      isEqual(
        replenishItemOption,
        REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
      ) &&
      !item.is_chargeable
    ) {
      return item;
    }

    if (
      isEqual(
        replenishItemOption,
        REPLENISH_APPLY_ITEMS_SCOPE.NON_CHARGEABLE_ONLY,
      ) &&
      item.is_chargeable
    ) {
      return item;
    }

    const selectedMeasureId: string = getSelectedMeasureId(item);

    const replenishQuantity: DecimalNumber = new DecimalNumber(
      get(itemThreshold, replenishType) || 0,
    )
      .mul(replenishPercent)
      .div(100);

    // Gets QohTo value and PendingQtyTo value for current item
    const [qohTo, pendingQtyTo] = item.qohTo || [];
    // Gets the factor value for selected measure.
    const selectedMeasureFactorValue: DecimalNumber = new DecimalNumber(
      findMeasureFactorValue(item, selectedMeasureId),
    );
    // Gets measure factor value and config value for threshold
    const thresholdsMeasureFactorValue: DecimalNumber = new DecimalNumber(
      findMeasureFactorValue(item, itemThreshold?.measure_entity_id),
    );
    const formattedPendingQtyTo = new DecimalNumber(pendingQtyTo || 0);
    const formattedQohTo = new DecimalNumber(qohTo || 0);

    const replenishCalculationFormulaParams: ITransferReplenishCalculationFormulaParams =
      {
        replenishQuantity,
        selectedMeasureFactorValue,
        thresholdsMeasureFactorValue,
        pendingQtyTo: formattedPendingQtyTo,
        qohTo: formattedQohTo,
        replenishPar: new DecimalNumber(get(itemThreshold, replenishType)),
        replenishMin: new DecimalNumber(
          get(itemThreshold, REPLENISH_TYPE_VALUES.MIN),
        ),
      };
    const replenishmentValue: DecimalNumber = replenishByCalculationFormula(
      replenishCalculationFormulaParams,
    );

    return {
      ...item,
      [formFieldPath.initialCount]: replenishmentValue.valueOf(),
    };
  });
};

/**
 * @deprecated we need switch replenish to new version(replenishAutoRestockInventoryItems instead)
 */
export const getAutoRestockReplenishedInventoryItems = (
  autoRestockReplenishedParams: IAutoRestockReplenishParams,
): IQohInventoryItemVenue[] => {
  const {
    inventoryItems,
    standWorksheetItems,
    selectedEvent,
    lastEventOfDestination,
    itemOption,
    replenishPercent,
    standWorksheetItemsMap,
  } = autoRestockReplenishedParams;

  return map(inventoryItems, (item) => {
    if (
      isEqual(itemOption, REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY) &&
      !item.is_chargeable
    ) {
      return item;
    }

    if (
      isEqual(itemOption, REPLENISH_APPLY_ITEMS_SCOPE.NON_CHARGEABLE_ONLY) &&
      item.is_chargeable
    ) {
      return item;
    }

    const itemThreshold: Threshold = getItemThreshold(
      item,
      false,
      selectedEvent,
    );
    if (isNil(get(itemThreshold, REPLENISH_TYPE_VALUES.PAR))) {
      return item;
    }

    const selectedMeasureFactorValue = new DecimalNumber(
      findMeasureFactorValue(item, getSelectedMeasureId(item)),
    );
    const thresholdsMeasureFactorValue = new DecimalNumber(
      findMeasureFactorValue(item, itemThreshold?.measure_entity_id),
    );
    let replenishmentValue: DecimalNumber;
    replenishmentValue = replenishByCalculationFormulaForRestock({
      standWorksheetItems,
      lastEventOfDestination,
      replenishPercent,
      thresholdsMeasureFactorValue,
      selectedMeasureFactorValue,
      itemThreshold,
      standWorksheetItemsMap,
      item,
    });

    // Invalid value
    if (!replenishmentValue.isFinite) {
      replenishmentValue = new DecimalNumber(0);
    }

    const maxNumber = new DecimalNumber(
      MAX_NUMBER_FOR_TRANSFER_QUANTITY,
    ).floor();
    // over the maximum boundary
    if (replenishmentValue.gt(maxNumber)) {
      replenishmentValue = maxNumber;
    }

    if (replenishmentValue.lt(0)) {
      replenishmentValue = new DecimalNumber(0);
    }

    const minQty = new DecimalNumber(
      get(itemThreshold, REPLENISH_TYPE_VALUES.MIN),
    ).mul(thresholdsMeasureFactorValue);
    const [qohTo, pendingQtyTo] = item.qohTo || [];
    const formattedQohTo = new DecimalNumber(qohTo);
    // watch out! the minQty already are the result: min * factor value
    const formattedMinQty = new DecimalNumber(minQty);

    if (
      !isNil(minQty.valueOf()) &&
      gte(formattedQohTo.add(pendingQtyTo), 0) &&
      gte(formattedQohTo.add(pendingQtyTo), formattedMinQty.valueOf())
    ) {
      return {
        ...item,
        initial_count: 0,
      };
    }

    return {
      ...item,
      initial_count: replenishmentValue.valueOf(),
    };
  });
};

/**
 * only use for transfer submit condition.
 *
 * @param transferType transfer type
 * @param transferStatus transfer current type
 * @param stateService state service
 */
export const getAutoCompleteTransferNextStatus = (
  transferType: TRANSFER_TYPE,
  transferStatus: TRANSFER_STATUS_VALUE,
  stateService: StateService,
): TRANSFER_STATUS_VALUE.Completed | TRANSFER_STATUS_VALUE.Pending => {
  if (
    isOpenOrNewDuringAutoCompleteTransfer(
      transferType,
      transferStatus,
      stateService,
    )
  ) {
    return TRANSFER_STATUS_VALUE.Completed;
  }

  return TRANSFER_STATUS_VALUE.Pending;
};

export const isOpenOrNewDuringAutoCompleteTransfer = (
  transferType: TRANSFER_TYPE,
  transferStatus: TRANSFER_STATUS_VALUE,
  stateService: StateService,
): boolean => {
  const autoCompleteStatus = stateService.getDuringTransferAutoCompleteConfig();

  return (
    isEqual(TRANSFER_TYPE.DURING_EVENT_TRANSFER, transferType) &&
    includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.New],
      transferStatus,
    ) &&
    isEqual(AUTO_COMPLETE, autoCompleteStatus)
  );
};

export const filterInventoryItemsByItemOption = (
  inventoryItems: IQohInventoryItemVenue[],
  itemOption: IBulkReplenishOption['itemOption'],
): IQohInventoryItemVenue[] => {
  switch (itemOption) {
    case REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY:
      return filter(inventoryItems, (item) => item.is_chargeable);
    case REPLENISH_APPLY_ITEMS_SCOPE.NON_CHARGEABLE_ONLY:
      return filter(inventoryItems, (item) => !item.is_chargeable);
    case REPLENISH_APPLY_ITEMS_SCOPE.ALL_ITEMS:
    default:
      return inventoryItems;
  }
};

export const filterQohFromColumn = (
  canSelectModalColumns: IPdfTableHeader[],
): IPdfTableHeader[] => {
  return filter(
    canSelectModalColumns,
    (item) => !includes([TRANSFER_PDF_HEADER_VALUES.From], item.path),
  );
};

export const getQohSiteIdFieldByOverride = (
  showQohFromOverride: boolean,
  siteId: ObjectId,
): string => {
  if (!isNil(showQohFromOverride)) {
    return showQohFromOverride ? siteId : '';
  }

  return siteId;
  // eslint-disable-next-line max-lines
};

export const filterSelectedSites = (
  selectedSiteIds: ObjectId[],
  allSites: ISite[],
): ISite[] => {
  return filter(allSites, (site) => !includes(selectedSiteIds, site.id));
};

export const filterOutUsedSites = (
  allSites: ISite[],
  transferItems: ITransferData[],
): ISite[] => {
  const usedSiteIds = map(transferItems, 'site_id_to');

  return allSites.filter((site) => !includes(usedSiteIds, site.id));
};

export const getParQty = (
  inventoryItem: IQohInventoryItemVenue,
  selectedEvent: IEvent,
  isOutsideOfEventTransfer: boolean,
): number => {
  const targetThreshold: Threshold = getItemThreshold(
    inventoryItem,
    isOutsideOfEventTransfer,
    selectedEvent,
  );
  if (isNil(targetThreshold)) return;

  const parQty = get(targetThreshold, 'par_qty');
  const currentMeasureFactorValue =
    find(inventoryItem.origin_measures, {
      entity_id: inventoryItem?.measure_entity_id,
    }).factor_value || 1;
  const thresholdMeasureFactorValue =
    find(inventoryItem.origin_measures, (originMeasure) =>
      isEqual(originMeasure.entity_id, targetThreshold?.measure_entity_id),
    ).factor_value || 1;
  const convertedParQty = new DecimalNumber(parQty)
    .mul(thresholdMeasureFactorValue)
    .div(currentMeasureFactorValue);

  return convertedParQty.valueOf();
};

export const setParQtyColumnVisible = (
  listGridApi: GridApi,
  status: string,
): void => {
  const isFinalStatus = includes(TRANSFER_FINAL_STATUS, status);
  listGridApi?.['columnController']?.columnApi?.setColumnsVisible(
    ['par_qty'],
    !isFinalStatus,
  );
};

export const addParQtyColumn = (
  canSelectModalColumns: IPdfTableHeader[],
  curStatus: string,
): IPdfTableHeader[] => {
  if (some(TRANSFER_FINAL_STATUS, (status) => isEqual(curStatus, status))) {
    return canSelectModalColumns;
  }
  const transferUnitIndex = findIndex(canSelectModalColumns, (header) =>
    isEqual(header.title, I18n.Forms.TransferForm.TransferUnit),
  );

  canSelectModalColumns.splice(
    transferUnitIndex + 1,
    0,
    TRANSFER_PAR_QTY_COLUMN_HEADER,
  );

  return canSelectModalColumns;
};

export const getParQtyColumn = (
  formService: IBaseFormService<ENTITY_TYPES>,
): IXVIListColumn => {
  return {
    path: 'par_qty',
    title: I18n.Forms.TransferForm.PARQty,
    defaultView: true,
    minWidth: 80,
    valueFormatter: (params: ValueFormatterParams): string =>
      params.data.isLeaf
        ? formService.numberFormatService.getNumberFormatter(false)(params)
        : null,
    ...COLUMN_ALIGN_RIGHT,
  };
};

export const getDefaultOrSelectedUnitEntityId = (
  selectedId: string,
  sortMeasures: ExpandMeasure[],
): string => {
  return (
    selectedId ||
    get(
      getDefaultMeasure(sortMeasures, MEASURE_TRANSACTION.Transfer),
      ENTITY_ID_PATH,
    )
  );
};

export const getSelectedMeasureId = (
  item: IQohInventoryItemVenue,
  measurePath: string = 'origin_measures',
): string | null => {
  const selectedMeasureId = get(
    item,
    TRANSFER_FORM_FIELD_PATHS.measureEntityId,
  );

  if (!isEmpty(selectedMeasureId)) {
    return selectedMeasureId;
  }

  const measures = get(item, measurePath) || [];

  const transferMeasures = filter(
    measures,
    (measure) => measure.transactions.transfer,
  );

  if (isEmpty(transferMeasures)) {
    return null;
  }

  // if the document have the measureEntityId use it else use first value
  return get(
    getDefaultMeasure(transferMeasures, MEASURE_TRANSACTION.Transfer),
    ENTITY_ID_PATH,
  );
};

export const isOpenDraftTransfer = (params: {
  status: TRANSFER_STATUS_VALUE;
  source: DATA_SOURCE;
  isSend: boolean;
  i18nService: XVII18nService;
}): boolean => {
  const { status, source, isSend = false, i18nService } = params;

  return (
    status === TRANSFER_STATUS_VALUE.Open &&
    (source === i18nService.t(DATA_SOURCE_ENUM_OPTIONS[DATA_SOURCE.POS]) ||
      source === DATA_SOURCE.POS) &&
    !isSend
  );
};

export const transferStatusFormatter = (
  params: IPdfValueParams,
  options: {
    i18nService: XVII18nService;
  },
): string => {
  const statusKey = isOpenDraftTransfer({
    status: params?.data?.[TRANSFER_FORM_FIELD_PATHS.status],
    source: params?.data?.[TRANSFER_FORM_FIELD_PATHS.source],
    isSend: params?.data?.[TRANSFER_FORM_FIELD_PATHS.is_send],
    i18nService: options.i18nService,
  })
    ? I18n.Status.OpenDraftBrackets
    : TRANSFER_STATUS_ENUM_OPTIONS[params.value?.toString()];

  return options.i18nService.instant(statusKey);
};

export const addQtyPulledColumn = (
  canSelectModalColumns: IPdfTableHeader[],
  options: {
    preColumnPath: string;
    transferStatus: string | string[];
  },
): IPdfTableHeader[] => {
  const defaultSelected: boolean = !some(
    isArray(options.transferStatus)
      ? options.transferStatus
      : [options.transferStatus],
    (status) => !includes(TRANSFER_OPEN_PENDING_STATUS, status),
  );
  const quantityRequestedIndex = findIndex(canSelectModalColumns, (header) =>
    isEqual(header.path, options.preColumnPath),
  );
  if (quantityRequestedIndex !== -1) {
    canSelectModalColumns.splice(quantityRequestedIndex + 1, 0, {
      ...TRANSFER_QTY_PULLED_COLUMN_HEADER,
      defaultSelected,
    });
  }

  return canSelectModalColumns;
};
// eslint-disable-next-line max-lines
